/**
 * Form Validation Utilities
 * Provides validation functions for login and password reset forms
 */

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateEmail = (email, t) => {
  const requiredMsg = t ? t('validation.emailRequired') : 'Email is required';
  const invalidMsg = t ? t('validation.emailInvalid') : 'Please enter a valid email address';

  if (!email) {
    return { isValid: false, error: requiredMsg };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: invalidMsg };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate password
 * @param {string} password - Password to validate
 * @param {Object} options - Validation options
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePassword = (password, options = {}, t) => {
  const { minLength = 8, requireUppercase = false, requireLowercase = false, requireNumber = false, requireSpecial = false } = options;

  const requiredMsg = t ? t('validation.passwordRequired') : 'Password is required';

  if (!password) {
    return { isValid: false, error: requiredMsg };
  }

  const minLenMsg = t
    ? t('validation.passwordMinLength')
    : `Password must be at least ${minLength} characters`;
  if (password.length < minLength) {
    return { isValid: false, error: minLenMsg };
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }

  if (requireNumber && !/[0-9]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }

  if (requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one special character' };
  }

  return { isValid: true, error: '' };
};

/**
 * Password strength requirements for reset
 */
export const passwordRequirements = [
  {
    id: 'length',
    label: 'Minimum 8 characters',
    regex: /.{8,}/,
    test: (password) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    regex: /[A-Z]/,
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    regex: /[a-z]/,
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    label: 'One number',
    regex: /[0-9]/,
    test: (password) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'One special character (!@#$%^&*)',
    regex: /[!@#$%^&*(),.?":{}|<>]/,
    test: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
  },
];

/**
 * Check password strength
 * @param {string} password - Password to check
 * @returns {Object} { strength: string, score: number, requirements: Array }
 */
export const checkPasswordStrength = (password) => {
  if (!password) {
    return { strength: 'none', score: 0, requirements: [] };
  }

  const metRequirements = passwordRequirements.map((req) => ({
    ...req,
    met: req.test(password),
  }));

  const score = metRequirements.filter((req) => req.met).length;
  let strength = 'weak';

  if (score >= 5) {
    strength = 'strong';
  } else if (score >= 3) {
    strength = 'medium';
  }

  return {
    strength,
    score,
    requirements: metRequirements,
  };
};

/**
 * Validate password confirmation
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePasswordConfirmation = (password, confirmPassword, t) => {
  const requiredMsg = t ? t('validation.verificationCodeRequired') : 'Please confirm your password';
  const mismatchMsg = t ? t('validation.passwordMismatch') : 'Passwords do not match';

  if (!confirmPassword) {
    return { isValid: false, error: requiredMsg };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: mismatchMsg };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate verification code (6 digits)
 * @param {string} code - Verification code
 * @param {Function} t - Optional i18n translate function
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateVerificationCode = (code, t) => {
  const requiredMsg = t ? t('validation.verificationCodeRequired') : 'Verification code is required';

  if (!code) {
    return { isValid: false, error: requiredMsg };
  }

  if (!/^\d{6}$/.test(code)) {
    return { isValid: false, error: 'Code must be 6 digits' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate login form
 * @param {Object} formData - Form data { email, password }
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateLoginForm = (formData, t) => {
  const errors = {};

  const emailValidation = validateEmail(formData.email, t);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  const passwordValidation = validatePassword(formData.password, {}, t);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

const validationUtils = {
  validateEmail,
  validatePassword,
  validatePasswordConfirmation,
  validateVerificationCode,
  validateLoginForm,
  checkPasswordStrength,
  passwordRequirements,
};

export default validationUtils;